/**
 * services/sync/OperationCompactor.ts
 * Pre-export compaction of sync operations
 *
 * Coalesces multiple operations targeting the same (table, recordId, field)
 * into a single operation before export, reducing delta size and simplifying
 * import processing on receiving devices.
 *
 * Design principles:
 * - Pure function: no database access, no side effects
 * - Preserves winning ops' batchId for atomic apply semantics
 * - Uses LWW (timestamp, seq, deviceId) for winner selection
 * - Handles delete-vs-field-op precedence correctly
 */

import type { SyncOperation, CompactionResult } from '@/types/sync/SyncOperation';
import { makeRecordKey, compareOpPrecedence } from './OperationApplicator';

// Re-export for convenience
export type { CompactionResult } from '@/types/sync/SyncOperation';

/**
 * Special field key used to track delete operations in the winner map.
 * Delete operations target the entire record, not a specific field.
 */
const DELETE_FIELD_KEY = '__delete__';

/**
 * Get the field key for an operation (used in winner tracking).
 * Delete operations use a special key since they target the entire record.
 *
 * @param op - The sync operation
 * @returns Field key for winner map lookup
 */
function getFieldKey(op: SyncOperation): string {
  if (op.op === 'delete') {
    return DELETE_FIELD_KEY;
  }
  return op.field;
}

/**
 * Tracks the current winning operation for a specific field.
 */
interface FieldWinner {
  /** Index of winning operation in the original array */
  opIndex: number;
  /** Timestamp (for precedence comparison - primary key) */
  timestamp: string;
  /** Sequence number (for precedence comparison - secondary key) */
  seq: number;
  /** Device ID (for precedence comparison - tertiary tiebreaker) */
  deviceId: string;
}

/**
 * Tracks all winning operations for a single record.
 */
interface RecordWinners {
  /** Table name */
  table: string;
  /** Record ID */
  recordId: string;
  /** Winning delete operation (if any) */
  deleteWinner: FieldWinner | null;
  /** Winning field operations by field name */
  fieldWinners: Map<string, FieldWinner>;
}

/**
 * Compact operations by coalescing multiple operations on the same
 * (table, recordId, field) into a single operation.
 *
 * For each unique (table, recordId, field) combination, keeps only the
 * operation with highest precedence (timestamp, then seq, then deviceId as tiebreaker).
 *
 * Handles delete-vs-field-op resolution:
 * - If delete wins over all field ops: keep only delete
 * - If any field op wins over delete (resurrection): drop delete, keep field ops
 * - If no delete: keep all winning field ops
 *
 * **Validation Note**: This function assumes all input operations are well-formed
 * (valid SyncOperation shape). This is guaranteed when input comes from getPendingOps().
 * If feeding external/untrusted operations, validate with isValidOperation() first.
 *
 * @param operations - Array of operations to compact (typically from getPendingOps)
 * @returns CompactionResult with compacted ops, all original opIds, and dropped seqs
 *
 * @example
 * // Multiple sets to same field - keeps latest
 * compactOperations([
 *   { op: 'set', field: 'name', value: 'A', seq: 1, ... },
 *   { op: 'set', field: 'name', value: 'B', seq: 2, ... },
 * ]);
 * // Result: only the seq:2 operation
 *
 * @example
 * // Delete after field ops - keeps only delete
 * compactOperations([
 *   { op: 'set', field: 'name', value: 'A', seq: 1, ... },
 *   { op: 'delete', seq: 2, ... },
 * ]);
 * // Result: only the delete operation
 *
 * @example
 * // Field op after delete (resurrection) - drops delete
 * compactOperations([
 *   { op: 'delete', seq: 1, ... },
 *   { op: 'set', field: 'name', value: 'A', seq: 2, ... },
 * ]);
 * // Result: only the set operation (resurrection)
 */
export function compactOperations(operations: SyncOperation[]): CompactionResult {
  // Collect ALL original opIds for purge tracking
  const originalOpIds: string[] = operations.map((op) => op.opId);

  // Early return for empty input
  if (operations.length === 0) {
    return {
      operations: [],
      originalOpIds: [],
      droppedSeqsByDevice: {},
    };
  }

  // Phase 1 & 2: Group by record and track winners per field
  const recordWinnersMap = new Map<string, RecordWinners>();

  for (let i = 0; i < operations.length; i++) {
    const op = operations[i];
    if (!op) continue;

    const recordKey = makeRecordKey(op.table, op.recordId);
    const fieldKey = getFieldKey(op);

    // Get or create record winners entry
    let recordWinners = recordWinnersMap.get(recordKey);
    if (!recordWinners) {
      recordWinners = {
        table: op.table,
        recordId: op.recordId,
        deleteWinner: null,
        fieldWinners: new Map(),
      };
      recordWinnersMap.set(recordKey, recordWinners);
    }

    const newPrecedence = { timestamp: op.timestamp, seq: op.seq, deviceId: op.deviceId };

    if (fieldKey === DELETE_FIELD_KEY) {
      // Handle delete operation
      if (!recordWinners.deleteWinner) {
        recordWinners.deleteWinner = { opIndex: i, timestamp: op.timestamp, seq: op.seq, deviceId: op.deviceId };
      } else {
        const currentPrecedence = {
          timestamp: recordWinners.deleteWinner.timestamp,
          seq: recordWinners.deleteWinner.seq,
          deviceId: recordWinners.deleteWinner.deviceId,
        };
        if (compareOpPrecedence(newPrecedence, currentPrecedence) > 0) {
          recordWinners.deleteWinner = { opIndex: i, timestamp: op.timestamp, seq: op.seq, deviceId: op.deviceId };
        }
      }
    } else {
      // Handle field operation (set/clear)
      const currentWinner = recordWinners.fieldWinners.get(fieldKey);
      if (!currentWinner) {
        recordWinners.fieldWinners.set(fieldKey, { opIndex: i, timestamp: op.timestamp, seq: op.seq, deviceId: op.deviceId });
      } else {
        const currentPrecedence = { timestamp: currentWinner.timestamp, seq: currentWinner.seq, deviceId: currentWinner.deviceId };
        if (compareOpPrecedence(newPrecedence, currentPrecedence) > 0) {
          recordWinners.fieldWinners.set(fieldKey, { opIndex: i, timestamp: op.timestamp, seq: op.seq, deviceId: op.deviceId });
        }
      }
    }
  }

  // Phase 3: Resolve delete-vs-field-op precedence and collect winning indices
  const winningIndices = new Set<number>();

  for (const recordWinners of recordWinnersMap.values()) {
    const { deleteWinner, fieldWinners } = recordWinners;

    if (deleteWinner && fieldWinners.size > 0) {
      // Both delete and field ops exist - check for resurrection
      const deletePrecedence = { timestamp: deleteWinner.timestamp, seq: deleteWinner.seq, deviceId: deleteWinner.deviceId };
      let hasResurrection = false;

      for (const fieldWinner of fieldWinners.values()) {
        const fieldPrecedence = { timestamp: fieldWinner.timestamp, seq: fieldWinner.seq, deviceId: fieldWinner.deviceId };
        if (compareOpPrecedence(fieldPrecedence, deletePrecedence) > 0) {
          hasResurrection = true;
          break;
        }
      }

      if (hasResurrection) {
        // Resurrection: drop delete, keep only per-field winners that beat the delete.
        // Note: fieldWinners already contains only the winning op per field (from Phase 2).
        // We further filter to only include those that beat the delete's precedence.
        // Field ops that lost to the delete are dropped along with the delete itself.
        for (const fieldWinner of fieldWinners.values()) {
          const fieldPrecedence = { timestamp: fieldWinner.timestamp, seq: fieldWinner.seq, deviceId: fieldWinner.deviceId };
          if (compareOpPrecedence(fieldPrecedence, deletePrecedence) > 0) {
            winningIndices.add(fieldWinner.opIndex);
          }
        }
        // Delete is NOT added to winningIndices (dropped due to resurrection)
      } else {
        // Delete wins over all field ops - keep only delete
        winningIndices.add(deleteWinner.opIndex);
        // Note: field ops are NOT added to winningIndices (dropped)
      }
    } else if (deleteWinner) {
      // Only delete, no field ops
      winningIndices.add(deleteWinner.opIndex);
    } else {
      // Only field ops, no delete
      for (const fieldWinner of fieldWinners.values()) {
        winningIndices.add(fieldWinner.opIndex);
      }
    }
  }

  // Phase 4: Build output - collect winners and compute dropped seqs
  const droppedSeqsByDevice: Record<string, number[]> = {};
  const winningOps: SyncOperation[] = [];

  for (let i = 0; i < operations.length; i++) {
    const op = operations[i];
    if (!op) continue;

    if (winningIndices.has(i)) {
      winningOps.push(op);
    } else {
      // This op was dropped - track its seq
      let seqs = droppedSeqsByDevice[op.deviceId];
      if (!seqs) {
        seqs = [];
        droppedSeqsByDevice[op.deviceId] = seqs;
      }
      seqs.push(op.seq);
    }
  }

  // Sort winning ops by (deviceId, seq) for consistent output ordering
  winningOps.sort((a, b) => {
    if (a.deviceId !== b.deviceId) {
      // Locale-independent string comparison (consistent across devices)
      if (a.deviceId < b.deviceId) return -1;
      if (a.deviceId > b.deviceId) return 1;
      return 0;
    }
    return a.seq - b.seq;
  });

  // Sort dropped seqs for consistent output
  for (const deviceId of Object.keys(droppedSeqsByDevice)) {
    droppedSeqsByDevice[deviceId]?.sort((a, b) => a - b);
  }

  return {
    operations: winningOps,
    originalOpIds,
    droppedSeqsByDevice,
  };
}
